import { SupabaseClient } from '@supabase/supabase-js'
import {
  ActivitiesWithDeals,
  DealLineItemsWithJoins,
  InvoicesWithDeals,
  KpisWithTables,
  LineItemsWithJoins
} from '../custom_types'
import { Database, Enums, Json, Tables, TablesInsert } from '../db_types'
import {
  aggregateCreditedMetricValues,
  resolveCreditedConsumerRule,
  type CreditedConsumerTeamMembership
} from '../domains/credited_consumers'
import type { CreditSetupRuleAssignmentSubject } from '../domains/credit_setup_rule_assignments/resolver'
import Targets, { getTargetCurrency } from '../domains/targets'
import { SimulationTargetOverride } from '../simulation/contracts'
import {
  CreditedTargetEvidence,
  MetricValueToDelete,
  ParsedExecutionResult,
  RecordData
} from '../types'
import {
  CompleteDomainEventDeliveryParams,
  CompensationTierEvent,
  CompensationTierSnapshotWrite,
  CreditSetupRuleForMetric,
  CreditSetupRuleMetricValueReconciliation,
  CreditSetupRuleMetricValueBootstrapStatus,
  CreditedMetricValueRequest,
  CreditedMetricValueRow,
  GetOrganizationMetricsValuesParams,
  MetricValueCurrencySnapshot,
  CreditSetupRuleMetricValueScopeRow,
  DomainEventDelivery,
  PeriodRulesAffectedScopeRow,
  ProfileOrganizationActivePeriod,
  TeamMembershipEvent
} from './index'
import { DolfinSupabaseDbRpc } from './supabase'

const SYNTHETIC_SIMULATION_RULE_ACTIVE_PERIOD = '[1970-01-01,)'

type PeriodCompensationRuleRow = Awaited<
  ReturnType<DolfinSupabaseDbRpc['getPeriodCompensationRules']>
>[number]

type ForcedCompRuleRow = Awaited<
  ReturnType<DolfinSupabaseDbRpc['getRecordCompensationRules']>
>[number] & {
  compensation_rule_drafts?: Tables<'compensation_rule_drafts'> | null
  compensation_rule_versions: Array<
    Tables<'compensation_rule_versions'> & {
      compensation_rule_version_metrics: Tables<'compensation_rule_version_metrics'>[]
    }
  >
}

type ForcedPeriodCompRuleRow = PeriodCompensationRuleRow & {
  compensation_rule_drafts?: Tables<'compensation_rule_drafts'> | null
}

type TvpAllocationResult = Awaited<ReturnType<DolfinSupabaseDbRpc['getTvpAllocation']>>
type TvpAllocationResultRow = NonNullable<TvpAllocationResult>[number]
type DraftTvpAllocationRow = Pick<Tables<'tvp_allocation'>, 'percentage' | 'profile_id' | 'team_id'>

type SimulatorCreditSetup = Tables<'credit_setups'> & {
  metrics: Pick<Tables<'metrics'>, 'organization_id'>
}

const hydrateForcedCompRuleVersions = (
  rule: ForcedCompRuleRow,
  overrideDiagram: Json | null | undefined
) => {
  const resolvedDiagram = overrideDiagram ?? rule.compensation_rule_drafts?.diagram ?? {}

  if (rule.compensation_rule_versions.length > 0) {
    return rule.compensation_rule_versions.map(version => ({
      ...version,
      diagram: resolvedDiagram
    }))
  }

  if (!rule.compensation_rule_drafts) {
    return []
  }

  return [
    {
      active_period: SYNTHETIC_SIMULATION_RULE_ACTIVE_PERIOD,
      clawback_diagram: rule.compensation_rule_drafts.clawback_diagram,
      compensation_rule_id: rule.id,
      compensation_rule_version_metrics: [],
      created_at: rule.compensation_rule_drafts.created_at,
      diagram: resolvedDiagram,
      id: rule.compensation_rule_drafts.id,
      is_tvp_based: rule.compensation_rule_drafts.is_tvp_based,
      updated_at: rule.compensation_rule_drafts.updated_at,
      visible_if_scheduled: false
    }
  ]
}

const hydrateForcedPeriodCompRuleVersions = (
  rule: ForcedPeriodCompRuleRow,
  overrideDiagram: Json | null | undefined
): PeriodCompensationRuleRow['compensation_rule_versions'] => {
  const resolvedDiagram = overrideDiagram ?? rule.compensation_rule_drafts?.diagram ?? {}

  if (rule.compensation_rule_versions.length > 0) {
    return rule.compensation_rule_versions.map(version => ({
      ...version,
      compensation_rule_version_metrics: version.compensation_rule_version_metrics ?? [],
      diagram: resolvedDiagram
    }))
  }

  if (!rule.compensation_rule_drafts) {
    return []
  }

  return [
    {
      active_period: SYNTHETIC_SIMULATION_RULE_ACTIVE_PERIOD,
      clawback_diagram: rule.compensation_rule_drafts.clawback_diagram,
      compensation_rule_id: rule.id,
      compensation_rule_version_metrics: [],
      created_at: rule.compensation_rule_drafts.created_at,
      diagram: resolvedDiagram,
      id: rule.compensation_rule_drafts.id,
      is_tvp_based: rule.compensation_rule_drafts.is_tvp_based,
      updated_at: rule.compensation_rule_drafts.updated_at,
      visible_if_scheduled: false
    }
  ]
}

export type SimulatorDBRpcConfig = Record<
  `${'deals' | 'invoices' | 'activities' | 'kpis' | 'lineItems'}ToProcess`,
  RecordData[] | undefined
> & {
  dealLineItemsToProcess?: RecordData[]
  forceCompRule?: number
  forceCompRuleDiagram?: Json | null
  organizationId?: number
  creditSetupRules?: CreditSetupRuleForMetric[]
  creditSetups?: SimulatorCreditSetup[]
  targetOverrides?: SimulationTargetOverride[]
}

type SimulatedMetricValue = Tables<'metric_values'> & {
  organization_id: number
}

export class SimulatorDBRpc extends DolfinSupabaseDbRpc {
  private _currencyRates = new Map<string, number>()
  private _metricValuesToUpsert = new Set<TablesInsert<'metric_values'>>()
  private _metricValuesToDelete = new Set<
    Pick<TablesInsert<'metric_values'>, 'metric_id' | 'attribution_key'>
  >()
  private readonly _simulatedMetricValues = new Map<string, SimulatedMetricValue>()
  private readonly _creditSetupRuleMetricValueOverlay = new Map<number, Set<string>>()
  private readonly _creditSetupRuleAssignments = new Map<
    string,
    CreditSetupRuleAssignmentSubject[]
  >()
  private readonly _teamMembershipsInPeriod = new Map<string, CreditedConsumerTeamMembership[]>()
  private readonly _profileOrganizationActivePeriods = new Map<
    string,
    ProfileOrganizationActivePeriod[]
  >()
  private readonly _metricValueCurrencySnapshots = new Map<string, MetricValueCurrencySnapshot>()
  private readonly _creditedTargetEvidence = new Map<string, CreditedTargetEvidence[]>()
  private readonly _creditSetupRulesById = new Map<number, CreditSetupRuleForMetric>()
  private readonly dealsToProcess: Tables<'deals'>[] = []
  private readonly invoicesToProcess: InvoicesWithDeals[] = []
  private readonly activitiesToProcess: ActivitiesWithDeals[] = []
  private readonly kpisToProcess: KpisWithTables[] = []
  private readonly lineItemsToProcess: LineItemsWithJoins[] = []
  private readonly dealLineItemsToProcess: DealLineItemsWithJoins[] = []

  private readonly forceCompRule?: number
  private readonly forceCompRuleDiagram?: Json | null
  private readonly organizationId?: number
  private readonly creditSetupRules?: CreditSetupRuleForMetric[]
  private readonly creditSetups?: SimulatorCreditSetup[]
  private readonly targetOverrides: SimulationTargetOverride[]
  private readonly teamHistoryByProfile = new Map<string, TeamMembershipEvent[] | null>()
  private forcedCompRuleDraftId?: number | null

  private _results = new Map<Enums<'plan_source'>, ParsedExecutionResult[]>()

  constructor(client: SupabaseClient<Database>, config: SimulatorDBRpcConfig) {
    super(client)

    this.dealsToProcess = config.dealsToProcess as Tables<'deals'>[]
    this.invoicesToProcess = config.invoicesToProcess as InvoicesWithDeals[]
    this.activitiesToProcess = config.activitiesToProcess as ActivitiesWithDeals[]
    this.kpisToProcess = config.kpisToProcess as KpisWithTables[]
    this.lineItemsToProcess = config.lineItemsToProcess as LineItemsWithJoins[]
    this.dealLineItemsToProcess = (config.dealLineItemsToProcess ?? []) as DealLineItemsWithJoins[]

    this.forceCompRule = config.forceCompRule
    this.forceCompRuleDiagram = config.forceCompRuleDiagram
    this.organizationId = config.organizationId
    this.creditSetupRules = config.creditSetupRules
    this.creditSetups = config.creditSetups
    for (const rule of config.creditSetupRules ?? []) {
      this._creditSetupRulesById.set(rule.id, rule)
    }
    this.targetOverrides = config.targetOverrides ?? []
  }

  private resolveTeamIdFromHistory(events: TeamMembershipEvent[], date: string): number | null {
    const lookupDate = date.slice(0, 10)

    for (let i = events.length - 1; i >= 0; i--) {
      const event = events[i]

      if (event.event_date <= lookupDate) {
        return event.status === 'removed' ? null : event.team_id
      }
    }

    return null
  }

  private async getProfileTeamId(
    profileId: string,
    date: string,
    organizationId = this.organizationId
  ): Promise<number | null> {
    if (organizationId == null) {
      return null
    }

    const cacheKey = `${organizationId}:${profileId}`

    if (!this.teamHistoryByProfile.has(cacheKey)) {
      const teamEventsByProfile = await this.getOrganizationProfilesTeamEvents(organizationId, [
        profileId
      ])

      this.teamHistoryByProfile.set(cacheKey, teamEventsByProfile?.[profileId] ?? null)
    }

    const events = this.teamHistoryByProfile.get(cacheKey)
    if (!events || events.length === 0) {
      return null
    }

    return this.resolveTeamIdFromHistory(events, date)
  }

  private rememberForcedCompRuleDraft(rule: ForcedCompRuleRow | ForcedPeriodCompRuleRow) {
    this.forcedCompRuleDraftId = rule.compensation_rule_drafts?.id ?? null
  }

  private async getForcedCompRuleDraftId(): Promise<number | null> {
    if (!this.forceCompRule) {
      return null
    }

    if (this.forcedCompRuleDraftId !== undefined) {
      return this.forcedCompRuleDraftId
    }

    const { data, error } = await this.supabase
      .from('compensation_rule_drafts')
      .select('id')
      .eq('compensation_rule_id', this.forceCompRule)
      .limit(1)
      .maybeSingle()

    if (error) {
      console.error('Error fetching forced compensation rule draft:', error)
      this.forcedCompRuleDraftId = null
      return null
    }

    this.forcedCompRuleDraftId = data?.id ?? null
    return this.forcedCompRuleDraftId
  }

  private async getDraftTvpAllocation(
    profileId: string,
    organizationId: number,
    compensationRuleDraftId: number,
    compensationRuleVersionId: number
  ): Promise<TvpAllocationResult> {
    const referenceDate = new Date().toISOString().slice(0, 10)
    const teamId = await this.getProfileTeamId(profileId, referenceDate, organizationId)
    const { data, error } = await this.supabase
      .from('tvp_allocation')
      .select('percentage, profile_id, team_id')
      .eq('compensation_rule_draft_id', compensationRuleDraftId)

    if (error) {
      console.error('Error fetching draft TVP allocation:', error)
      return null
    }

    const allocations = ((data ?? []) as DraftTvpAllocationRow[]).filter(
      allocation =>
        allocation.profile_id === profileId ||
        (teamId != null && allocation.team_id === teamId) ||
        (allocation.profile_id == null && allocation.team_id == null)
    )

    const allocation =
      allocations.find(allocation => allocation.profile_id === profileId) ??
      allocations.find(
        allocation => allocation.profile_id == null && allocation.team_id === teamId
      ) ??
      allocations.find(allocation => allocation.profile_id == null && allocation.team_id == null)

    if (!allocation) {
      return []
    }

    return [
      {
        compensation_rule_version_id: compensationRuleVersionId,
        percentage: allocation.percentage,
        profile_id: profileId,
        reference_date: referenceDate,
        team_id: teamId
      } as TvpAllocationResultRow
    ]
  }

  private async resolveTargetOverride(
    targetId: number,
    profileId: string,
    periodStart: string,
    periodEnd: string
  ): Promise<SimulationTargetOverride | null> {
    if (this.targetOverrides.length === 0) {
      return null
    }

    const matchingOverrides = this.targetOverrides.filter(
      override =>
        override.targetId === targetId &&
        override.periodStart === periodStart &&
        override.periodEnd === periodEnd
    )

    if (matchingOverrides.length === 0) {
      return null
    }

    const profileOverride =
      matchingOverrides.find(
        override => override.scope.type === 'profile' && override.scope.profileId === profileId
      ) ?? null

    if (profileOverride) {
      return profileOverride
    }

    const teamId = await this.getProfileTeamId(profileId, periodStart)
    if (teamId != null) {
      const teamOverride =
        matchingOverrides.find(
          override => override.scope.type === 'team' && override.scope.teamId === teamId
        ) ?? null

      if (teamOverride) {
        return teamOverride
      }
    }

    return matchingOverrides.find(override => override.scope.type === 'global') ?? null
  }

  private async applyTargetAmountOverride<
    TRow extends {
      achievement?: number
      allow_partial_outcome?: boolean
      global_target?: boolean
      is_upper_bound?: boolean
      period_end?: string | null
      period_start?: string | null
      target_amount?: number
      total_achievement?: number
      total_achievement_percentage?: number
      total_period_amount?: number
      total_value?: number
    }
  >(params: {
    periodEnd: string
    periodStart: string
    profileId: string
    row: TRow
    targetId: number
  }): Promise<TRow> {
    const override = await this.resolveTargetOverride(
      params.targetId,
      params.profileId,
      params.periodStart,
      params.periodEnd
    )

    if (!override) {
      return params.row
    }

    const nextRow = {
      ...params.row
    }
    const targetAmount = override.targetAmount

    if ('target_amount' in nextRow) {
      nextRow.target_amount = targetAmount as TRow['target_amount']
    }

    if ('total_period_amount' in nextRow) {
      nextRow.total_period_amount = targetAmount as TRow['total_period_amount']
    }

    if ('achievement' in nextRow && nextRow.total_value !== undefined) {
      nextRow.achievement = (
        targetAmount === 0
          ? 0
          : Targets.computeTargetAchievement(
              targetAmount,
              Boolean(nextRow.is_upper_bound),
              Boolean(nextRow.allow_partial_outcome),
              nextRow.total_value
            )
      ) as TRow['achievement']
    }

    if ('total_achievement_percentage' in nextRow && nextRow.total_achievement !== undefined) {
      nextRow.total_achievement_percentage = (
        targetAmount === 0
          ? 0
          : Targets.computeTargetAchievement(
              targetAmount,
              Boolean(nextRow.is_upper_bound),
              Boolean(nextRow.allow_partial_outcome),
              nextRow.total_achievement
            )
      ) as TRow['total_achievement_percentage']
    }

    return nextRow
  }

  override async claimDomainEventDeliveries(_params: {
    consumer: string
    eventType: string
    limit?: number
  }): Promise<DomainEventDelivery[]> {
    return []
  }

  override async completeDomainEventDelivery(
    _params: CompleteDomainEventDeliveryParams
  ): Promise<void> {}

  async recordCalculationFailure(
    _source: Enums<'plan_source'>,
    _organizationId: number,
    _recordIds: number[],
    _reason: string
  ): Promise<void> {}

  override async saveResults(
    results: ParsedExecutionResult[],
    source?:
      | 'deals'
      | 'activities'
      | 'invoices'
      | 'kpis'
      | 'line_items'
      | 'deal_line_items'
      | 'custom_records'
  ): Promise<Error | void> {
    this._results.set(source as Enums<'plan_source'>, results)
  }
  override async saveCompensationTierSnapshots(
    _snapshots: CompensationTierSnapshotWrite[]
  ): Promise<CompensationTierEvent[]> {
    return []
  }

  override async getPendingProcessingDeals(organizationId?: number): Promise<Tables<'deals'>[]> {
    if (!organizationId) return this.dealsToProcess
    return this.dealsToProcess?.filter(d => d.organization_id === organizationId) ?? []
  }

  override async getPendingProcessingInvoices(
    organizationId?: number
  ): Promise<InvoicesWithDeals[]> {
    if (!organizationId) return this.invoicesToProcess
    return this.invoicesToProcess?.filter(i => i.organization_id === organizationId) ?? []
  }

  override async getPendingProcessingActivities(
    organizationId?: number
  ): Promise<ActivitiesWithDeals[]> {
    if (!organizationId) return this.activitiesToProcess
    return this.activitiesToProcess?.filter(a => a.organization_id === organizationId) ?? []
  }

  override async getPendingProcessingKpis(organizationId?: number): Promise<Tables<'kpis'>[]> {
    if (!organizationId) return this.kpisToProcess
    return this.kpisToProcess?.filter(k => k.organization_id === organizationId) ?? []
  }

  override async getPendingProcessingLineItems(
    organizationId?: number
  ): Promise<LineItemsWithJoins[]> {
    if (!organizationId) return this.lineItemsToProcess
    return this.lineItemsToProcess?.filter(li => li.organization_id === organizationId) ?? []
  }

  override async getPendingProcessingDealLineItems(
    organizationId?: number
  ): Promise<DealLineItemsWithJoins[]> {
    if (!organizationId) return this.dealLineItemsToProcess
    return (
      this.dealLineItemsToProcess?.filter(item => item.organization_id === organizationId) ?? []
    )
  }

  // save methods should not write to the database but to an in-memory state
  override async saveCurrencyRate(from: string, to: string, rate: number, daterange: string) {
    const key = `${from}-${to}-${daterange}`
    this._currencyRates.set(key, rate)

    return undefined
  }

  // if currency rate is in memory it should be returned, if not use parent method
  override async getFixedCurrencyRate(
    from: string,
    to: string,
    date: string,
    organizationId: number
  ) {
    const key = `${from}-${to}-${date}`
    if (this._currencyRates.has(key)) {
      return this._currencyRates.get(key)!
    }

    return super.getFixedCurrencyRate(from, to, date, organizationId)
  }

  override async saveMetricValues(
    _: Enums<'plan_source'>,
    values: TablesInsert<'metric_values'>[]
  ): Promise<Error | Tables<'metric_values'>[]> {
    const savedValues = values.map(value => {
      const identity = this.getSimulatedMetricValueIdentity(
        value.metric_id!,
        value.attribution_key!
      )
      const existing = this._simulatedMetricValues.get(identity)
      const savedValue = {
        ...value,
        id: existing?.id ?? this.getNextSimulatedMetricValueId(),
        created_at: existing?.created_at ?? new Date().toISOString(),
        updated_at: new Date().toISOString()
      } as Tables<'metric_values'>

      this._simulatedMetricValues.set(identity, {
        ...savedValue,
        organization_id: this.organizationId ?? 0
      })
      this._metricValuesToUpsert.add(value)
      return savedValue
    })

    return savedValues
  }

  override async saveMetricValueCurrencies(
    conversions: MetricValueCurrencySnapshot[]
  ): Promise<number> {
    for (const conversion of conversions) {
      this._metricValueCurrencySnapshots.set(
        `${conversion.metric_value_id}:${conversion.currency}`,
        conversion
      )
    }

    return conversions.length
  }

  override async upsertPeriodRulesAffectedScope(
    _rows: PeriodRulesAffectedScopeRow[]
  ): Promise<number> {
    return 0
  }

  private nextSimulatedMetricValueId = -1

  private getNextSimulatedMetricValueId() {
    return this.nextSimulatedMetricValueId--
  }

  private getSimulatedMetricValueIdentity(
    metricId: number,
    attributionKey: string | null | undefined
  ) {
    return `${metricId}:${attributionKey ?? ''}`
  }

  override async getActiveCreditSetupRulesForMetric(
    sourceMetricId: number,
    organizationId: number
  ): Promise<CreditSetupRuleForMetric[]> {
    return this.getActiveCreditSetupRulesForMetrics([sourceMetricId], organizationId)
  }

  override async getActiveCreditSetupRulesForMetrics(
    sourceMetricIds: number[],
    organizationId: number
  ): Promise<CreditSetupRuleForMetric[]> {
    const sourceMetricIdSet = new Set(sourceMetricIds)
    let rules: CreditSetupRuleForMetric[]
    if (this.creditSetupRules) {
      rules = this.creditSetupRules.filter(
        rule =>
          sourceMetricIdSet.has(rule.credit_setups.source_metric_id) &&
          rule.credit_setups.metrics.organization_id === organizationId &&
          rule.archived_at == null &&
          rule.credit_setups.archived_at == null
      )
    } else {
      rules = await super.getActiveCreditSetupRulesForMetrics(sourceMetricIds, organizationId)
    }

    for (const rule of rules) {
      this._creditSetupRulesById.set(rule.id, rule)
    }

    return rules
  }

  override async getCreditSetupSourceMetricIdsWithHistory(
    sourceMetricIds: number[],
    organizationId: number
  ): Promise<number[]> {
    if (this.creditSetups === undefined && this.creditSetupRules === undefined) {
      return super.getCreditSetupSourceMetricIdsWithHistory(sourceMetricIds, organizationId)
    }

    const sourceMetricIdSet = new Set(sourceMetricIds)
    const setupMetricIds = (this.creditSetups ?? [])
      .filter(
        setup =>
          setup.metrics.organization_id === organizationId &&
          sourceMetricIdSet.has(setup.source_metric_id)
      )
      .map(setup => setup.source_metric_id)

    const ruleMetricIds = (this.creditSetupRules ?? [])
      .filter(
        rule =>
          sourceMetricIdSet.has(rule.credit_setups.source_metric_id) &&
          rule.credit_setups.metrics.organization_id === organizationId
      )
      .map(rule => rule.credit_setups.source_metric_id)

    return [...new Set([...setupMetricIds, ...ruleMetricIds])]
  }

  override async enqueueMetricRecordsPendingProcessing(_metricId: number): Promise<void> {
    return undefined
  }

  override async getCreditSetupRuleMetricValueBootstrapStatus(
    _organizationId: number,
    _sourceMetricIds: number[]
  ): Promise<CreditSetupRuleMetricValueBootstrapStatus[]> {
    return []
  }

  override async getCreditSetupRuleMetricValueScope(
    organizationId: number,
    sourceMetricId: number,
    attributionKeys: string[]
  ): Promise<CreditSetupRuleMetricValueScopeRow[]> {
    const allowedKeys = new Set(attributionKeys)
    const rows: CreditSetupRuleMetricValueScopeRow[] = []

    for (const [ruleId, identities] of this._creditSetupRuleMetricValueOverlay) {
      for (const identity of identities) {
        const value = this._simulatedMetricValues.get(identity)
        const matchesRequestedAttribution = Array.from(allowedKeys).some(
          key =>
            value?.attribution_key === key || value?.attribution_key?.startsWith(`${key}:deal:`)
        )
        if (
          !value ||
          value.organization_id !== organizationId ||
          value.metric_id !== sourceMetricId ||
          !matchesRequestedAttribution
        ) {
          continue
        }

        const rule = this._creditSetupRulesById.get(ruleId)
        if (!rule) {
          continue
        }

        rows.push({
          accrual_date: value.accrual_date,
          attribution_key: value.attribution_key,
          credit_setup_id: rule.credit_setup_id,
          credit_setup_rule_id: rule.id,
          metric_id: value.metric_id,
          metric_value_id: value.id,
          organization_id: organizationId,
          profile_id: value.profile_id
        })
      }
    }

    return rows
  }

  private async getSimulationCreditSetupRuleAssignments(requests: CreditedMetricValueRequest[]) {
    const setupKeys = new Map<string, { organizationId: number; creditSetupId: number }>()

    for (const request of requests) {
      setupKeys.set(`${request.organizationId}:${request.creditSetupId}`, {
        organizationId: request.organizationId,
        creditSetupId: request.creditSetupId
      })
    }

    await Promise.all(
      [...setupKeys.entries()].map(async ([key, params]) => {
        if (this._creditSetupRuleAssignments.has(key)) return

        const assignments = await this.listCreditSetupRuleAssignments(params)
        this._creditSetupRuleAssignments.set(key, assignments)
      })
    )

    return this._creditSetupRuleAssignments
  }

  private async getSimulationTeamMemberships(
    request: CreditedMetricValueRequest
  ): Promise<CreditedConsumerTeamMembership[]> {
    if (!request.profileId) {
      return request.teamIds.map(teamId => ({
        teamId,
        effectiveFrom: request.startDate,
        effectiveTo: null
      }))
    }

    const teamScope = [...new Set(request.teamIds ?? [])]
      .sort((left, right) => left - right)
      .join(',')
    const key = [
      request.organizationId,
      request.profileId,
      request.startDate,
      request.endDate,
      teamScope
    ].join(':')
    const cached = this._teamMembershipsInPeriod.get(key)
    if (cached) return cached

    const memberships = await this.getTeamMembershipsInPeriod({
      organizationId: request.organizationId,
      start: request.startDate,
      end: request.endDate,
      teamIds: request.teamIds
    })
    const result = memberships
      .filter(membership => membership.profileId === request.profileId)
      .map(membership => ({
        teamId: membership.teamId,
        effectiveFrom: membership.activeFrom,
        effectiveTo: membership.activeUntil ?? null
      }))
    this._teamMembershipsInPeriod.set(key, result)

    return result
  }

  private async getSimulationProfileOrganizationActivePeriods(
    request: CreditedMetricValueRequest
  ): Promise<ProfileOrganizationActivePeriod[]> {
    if (!request.profileId) return []

    const key = [
      request.organizationId,
      request.profileId,
      request.startDate,
      request.endDate
    ].join(':')
    const cached = this._profileOrganizationActivePeriods.get(key)
    if (cached) return cached

    const periods = await this.getProfileOrganizationActivePeriods({
      organizationId: request.organizationId,
      profileId: request.profileId,
      start: request.startDate,
      end: request.endDate
    })
    this._profileOrganizationActivePeriods.set(key, periods)
    return periods
  }

  private async getSimulatedCreditedMetricValues(
    requests: CreditedMetricValueRequest[]
  ): Promise<CreditedMetricValueRow[]> {
    const simulatedValues = [...this._simulatedMetricValues.values()]
    if (simulatedValues.length === 0) return []

    const assignmentsBySetup = await this.getSimulationCreditSetupRuleAssignments(requests)
    const rows: CreditedMetricValueRow[] = []

    for (const [requestId, request] of requests.entries()) {
      const assignments = assignmentsBySetup.get(
        `${request.organizationId}:${request.creditSetupId}`
      )
      if (!assignments) continue

      const activePeriods = await this.getSimulationProfileOrganizationActivePeriods(request)

      const values = simulatedValues.filter(
        value =>
          value.organization_id === request.organizationId &&
          value.metric_id === request.metricId &&
          value.accrual_date >= request.startDate &&
          value.accrual_date <= request.endDate &&
          (request.profileId == null ||
            activePeriods.some(
              period =>
                value.accrual_date >= period.activeFrom &&
                (period.activeUntil == null || value.accrual_date < period.activeUntil)
            )) &&
          (request.valueProfileIds == null ||
            request.valueProfileIds.length === 0 ||
            request.valueProfileIds.includes(value.profile_id ?? '')) &&
          (!request.valueTeamIds?.length || request.valueTeamIds.includes(value.team_id ?? 0)) &&
          (request.sourceIdLimitOnLastDay == null ||
            value.accrual_date < request.endDate ||
            (() => {
              const sourceId = Number(value.attribution_key.split(':')[1])
              return Number.isSafeInteger(sourceId) && sourceId <= request.sourceIdLimitOnLastDay
            })())
      )

      if (values.length === 0) continue

      const activeAssignments = assignments.filter(assignment => {
        const rule = this._creditSetupRulesById.get(assignment.credit_setup_rule_id)
        return (
          rule?.credit_setup_id === request.creditSetupId &&
          rule.credit_setups.source_metric_id === request.metricId &&
          rule.archived_at == null &&
          rule.credit_setups.archived_at == null
        )
      })
      const teamMemberships =
        request.teamMemberships ?? (await this.getSimulationTeamMemberships(request))
      for (const value of values) {
        const matchingRuleIds = new Set<number>()
        for (const [ruleId, identities] of this._creditSetupRuleMetricValueOverlay) {
          if (
            identities.has(
              this.getSimulatedMetricValueIdentity(value.metric_id, value.attribution_key)
            )
          ) {
            matchingRuleIds.add(ruleId)
          }
        }

        const resolution = resolveCreditedConsumerRule({
          assignments: activeAssignments,
          creditSetupId: request.creditSetupId,
          profileId: request.profileId,
          teamMemberships,
          accrualDate: value.accrual_date
        })

        const rule = resolution ? this._creditSetupRulesById.get(resolution.ruleId) : undefined
        if (
          !resolution ||
          !rule ||
          rule.credit_setup_id !== request.creditSetupId ||
          rule.credit_setups.source_metric_id !== request.metricId ||
          rule.archived_at != null ||
          rule.credit_setups.archived_at != null ||
          !matchingRuleIds.has(resolution.ruleId)
        ) {
          continue
        }

        const currencySnapshot = request.targetCurrency
          ? this._metricValueCurrencySnapshots.get(`${value.id}:${request.targetCurrency}`)
          : undefined

        rows.push({
          accrual_date: value.accrual_date,
          assignment_id: resolution.assignment.id,
          attribution_key: value.attribution_key,
          converted_at: currencySnapshot?.converted_at ?? value.converted_at ?? value.accrual_date,
          conversion_kind: currencySnapshot?.conversion_kind ?? 'fallback',
          credit_setup_id: request.creditSetupId,
          credit_setup_rule_id: resolution.ruleId,
          currency: (currencySnapshot?.currency ?? value.currency)!,
          exchange_rate: (currencySnapshot?.exchange_rate ?? value.exchange_rate)!,
          metric_id: value.metric_id,
          metric_value_id: value.id,
          organization_id: request.organizationId,
          original_value: value.original_value!,
          profile_id: request.profileId,
          request_id: requestId,
          team_id: (resolution.subject === 'team' ? resolution.assignment.team_id : null)!,
          value: currencySnapshot?.value ?? value.value
        })
      }
    }

    return rows
  }

  override async getCreditedMetricValues(
    requests: CreditedMetricValueRequest[]
  ): Promise<CreditedMetricValueRow[]> {
    const persistedRows = await super.getCreditedMetricValues(requests)
    const simulatedKeys = new Set(
      [...this._simulatedMetricValues.values(), ...this._metricValuesToDelete].map(value =>
        this.getSimulatedMetricValueIdentity(value.metric_id!, value.attribution_key!)
      )
    )
    const rows = persistedRows.filter(
      row =>
        !simulatedKeys.has(this.getSimulatedMetricValueIdentity(row.metric_id, row.attribution_key))
    )
    const simulatedRows = await this.getSimulatedCreditedMetricValues(requests)

    return [...rows, ...simulatedRows]
  }

  override async reconcileCreditSetupRuleMetricValues(
    reconciliations: CreditSetupRuleMetricValueReconciliation[],
    _affectedScope: PeriodRulesAffectedScopeRow[] = []
  ): Promise<number> {
    let matchedCount = 0

    for (const reconciliation of reconciliations) {
      const overlay =
        this._creditSetupRuleMetricValueOverlay.get(reconciliation.credit_setup_rule_id) ??
        new Set<string>()
      const matchingIds = new Set(reconciliation.matching_metric_value_ids)

      for (const candidateId of reconciliation.candidate_metric_value_ids) {
        const identity = this.findSimulatedMetricValueIdentity(candidateId)
        if (!identity) {
          continue
        }

        if (matchingIds.has(candidateId)) {
          overlay.add(identity)
          matchedCount += 1
        } else {
          overlay.delete(identity)
        }
      }

      this._creditSetupRuleMetricValueOverlay.set(reconciliation.credit_setup_rule_id, overlay)
    }

    return matchedCount
  }

  private findSimulatedMetricValueIdentity(metricValueId: number) {
    for (const [identity, value] of this._simulatedMetricValues) {
      if (value.id === metricValueId) {
        return identity
      }
    }

    return null
  }

  override async deleteMetricValues(values: MetricValueToDelete[]) {
    for (const value of values) {
      const identity = this.getSimulatedMetricValueIdentity(
        value.metric_id!,
        value.attribution_key ?? ''
      )
      const simulatedValueId = this._simulatedMetricValues.get(identity)?.id
      this._simulatedMetricValues.delete(identity)
      if (simulatedValueId != null) {
        for (const key of this._metricValueCurrencySnapshots.keys()) {
          if (key.startsWith(`${simulatedValueId}:`)) {
            this._metricValueCurrencySnapshots.delete(key)
          }
        }
      }
      for (const overlay of this._creditSetupRuleMetricValueOverlay.values()) {
        overlay.delete(identity)
      }
      this._metricValuesToDelete.add({
        metric_id: value.metric_id!,
        attribution_key: value.attribution_key ?? ''
      })
    }

    return undefined
  }

  override async deleteCrmRecordsAndReferences(_params: {
    organizationId: number
    tableName: string
    recordIds: string[]
  }): Promise<void> {}

  override async getMetricsValues(
    metricIds: number[],
    periodStart: string,
    endDate: string,
    organizationId: number,
    profileIds?: string[],
    teamIds?: number[],
    sourceId?: number
  ) {
    return super.getMetricsValues(
      metricIds,
      periodStart,
      endDate,
      organizationId,
      profileIds,
      teamIds,
      sourceId,
      Array.from(this._metricValuesToUpsert),
      Array.from(this._metricValuesToDelete)
    )
  }

  override async getOrganizationMetricsValues({
    metricIds,
    startDate,
    endDate,
    organizationId,
    sourceId,
    metricsToUpsert,
    metricsToExclude,
    groupBy,
    cumulative,
    targetCurrency
  }: GetOrganizationMetricsValuesParams) {
    // Use the same in-memory metric mutation overlay as scoped simulation
    // queries, while intentionally leaving both dimensions unfiltered.
    return super.getMetricsValues(
      metricIds,
      startDate,
      endDate,
      organizationId,
      undefined,
      undefined,
      sourceId,
      [...(metricsToUpsert ?? []), ...Array.from(this._metricValuesToUpsert)],
      [...(metricsToExclude ?? []), ...Array.from(this._metricValuesToDelete)],
      groupBy,
      cumulative,
      targetCurrency
    )
  }

  private async getSimulationTargetCurrency(
    target: Tables<'targets'>,
    profileId: string,
    referenceDate: string
  ) {
    if (target.currency_mode === 'organization') {
      return undefined
    }

    const orgCurrency = (await this.getOrgCurrency(target.organization_id)).currency ?? undefined
    const team =
      target.currency_mode === 'team'
        ? await this.getTeam(profileId, target.organization_id, referenceDate)
        : undefined

    return getTargetCurrency({
      currencyMode: target.currency_mode,
      targetCurrency: target.currency,
      orgCurrency,
      teamId: team?.id,
      teamCurrency: team?.currency ?? undefined
    })
  }

  private getCreditedTargetEvidenceKey(params: {
    targetId: number
    profileId: string
    start: string
    end: string
    effectiveDate?: string
    currentEntityId?: number
  }) {
    return [
      params.targetId,
      params.profileId,
      params.start,
      params.end,
      params.effectiveDate ?? '',
      params.currentEntityId ?? ''
    ].join(':')
  }

  private buildCreditedTargetEvidence(
    targetId: number,
    request: CreditedMetricValueRequest,
    rows: CreditedMetricValueRow[]
  ): CreditedTargetEvidence[] {
    const contributionsByRule = new Map<string, CreditedTargetEvidence>()

    for (const row of rows) {
      const key = `${row.credit_setup_rule_id}:${row.assignment_id}:${row.team_id ?? ''}`
      const evidence = contributionsByRule.get(key) ?? {
        consumerKind: 'target',
        targetId,
        metricId: request.metricId,
        creditSetupId: request.creditSetupId,
        creditSetupRuleId: row.credit_setup_rule_id,
        assignmentId: row.assignment_id,
        profileId: request.profileId,
        teamId: row.team_id,
        effectiveFrom: request.startDate,
        effectiveTo: request.endDate,
        noRuleApplied: false,
        contributions: []
      }
      evidence.contributions.push({
        accrualDate: row.accrual_date,
        assignmentId: row.assignment_id,
        attributionKey: row.attribution_key,
        convertedAt: row.converted_at,
        conversionKind: row.conversion_kind as 'converted' | 'zero' | 'fallback',
        creditSetupRuleId: row.credit_setup_rule_id,
        currency: row.currency,
        exchangeRate: row.exchange_rate,
        metricValueId: row.metric_value_id,
        originalValue: row.original_value,
        selectedValue: row.value,
        targetCurrency: request.targetCurrency
      })
      contributionsByRule.set(key, evidence)
    }

    if (contributionsByRule.size > 0) return [...contributionsByRule.values()]

    return [
      {
        consumerKind: 'target',
        targetId,
        metricId: request.metricId,
        creditSetupId: request.creditSetupId,
        creditSetupRuleId: null,
        assignmentId: null,
        profileId: request.profileId,
        teamId: null,
        effectiveFrom: request.startDate,
        effectiveTo: request.endDate,
        noRuleApplied: true,
        contributions: []
      }
    ]
  }

  private async applySimulatedCreditedTargetValue<
    TRow extends {
      achievement?: number
      allow_partial_outcome?: boolean
      global_target?: boolean
      is_upper_bound?: boolean
      metric_id?: number | null
      period_end?: string | null
      period_start?: string | null
      team_ids?: number[] | null
      team_target?: boolean
      target_amount?: number
      total_achievement?: number
      total_achievement_percentage?: number
      total_period_amount?: number
      total_value?: number
    }
  >(
    targetId: number,
    profileId: string,
    end: string,
    effectiveDate: string | undefined,
    currentEntityId: number | undefined,
    row: TRow
  ): Promise<{ row: TRow; creditedEvidence: CreditedTargetEvidence[] }> {
    if (
      this._metricValuesToUpsert.size === 0 &&
      this._metricValuesToDelete.size === 0 &&
      this._creditSetupRuleMetricValueOverlay.size === 0
    ) {
      return { row, creditedEvidence: [] }
    }

    const target = await this.getTarget(targetId)
    if (
      !target?.credit_setup_id ||
      target.metric_id == null ||
      !target.metrics ||
      row.metric_id == null
    ) {
      return { row, creditedEvidence: [] }
    }
    const targetCurrency = await this.getSimulationTargetCurrency(
      target,
      profileId,
      effectiveDate ?? row.period_end ?? end
    )
    const teamIds = row.team_ids ?? target.teams_targets?.map(team => team.team_id) ?? []
    const request: CreditedMetricValueRequest = {
      organizationId: target.organization_id,
      metricId: target.metric_id,
      creditSetupId: target.credit_setup_id,
      profileId,
      teamIds,
      valueProfileIds: null,
      valueTeamIds: undefined,
      sourceIdLimitOnLastDay: currentEntityId,
      targetCurrency,
      callSite: 'simulation_target',
      startDate: row.period_start ?? end,
      endDate:
        effectiveDate != null && currentEntityId != null ? effectiveDate : (row.period_end ?? end)
    }
    const values = await this.getCreditedMetricValues([request])
    const totalValue = aggregateCreditedMetricValues(values, target.metrics.aggregate_function)

    const targetAmount = row.target_amount ?? row.total_period_amount
    const achievement =
      targetAmount == null
        ? row.achievement
        : Targets.computeTargetAchievement(
            targetAmount,
            Boolean(row.is_upper_bound),
            Boolean(row.allow_partial_outcome),
            totalValue
          )

    return {
      row: {
        ...row,
        total_value: totalValue,
        achievement,
        ...(Object.prototype.hasOwnProperty.call(row, 'total_achievement')
          ? {
              total_achievement: totalValue,
              total_achievement_percentage: achievement
            }
          : {})
      },
      creditedEvidence: this.buildCreditedTargetEvidence(targetId, request, values)
    }
  }

  private async applySimulatedCreditedCumulativeValue<
    TRow extends {
      allow_partial_outcome?: boolean
      is_upper_bound?: boolean
      metric_id?: number | null
      period_end?: string | null
      period_start?: string | null
      target_id?: number
      target_currency?: string | null
      company_target?: boolean | null
      global_target?: boolean | null
      team_target?: boolean | null
      team_ids?: number[] | null
      total_achievement?: number
      total_achievement_percentage?: number
      total_period_amount?: number
    }
  >(targetId: number, profileId: string, start: string, end: string, row: TRow) {
    if (
      this._metricValuesToUpsert.size === 0 &&
      this._metricValuesToDelete.size === 0 &&
      this._creditSetupRuleMetricValueOverlay.size === 0
    ) {
      return row
    }

    const target = await this.getTarget(targetId)
    if (
      !target?.credit_setup_id ||
      target.metric_id == null ||
      !target.metrics ||
      row.metric_id == null
    ) {
      return row
    }
    const targetCurrency = await this.getSimulationTargetCurrency(
      target,
      profileId,
      row.period_end ?? end
    )

    const teamIds = row.team_ids ?? target.teams_targets?.map(team => team.team_id) ?? []
    const values = await this.getCreditedMetricValues([
      {
        organizationId: target.organization_id,
        metricId: target.metric_id,
        creditSetupId: target.credit_setup_id,
        profileId,
        teamIds,
        valueProfileIds: null,
        valueTeamIds: undefined,
        targetCurrency: row.target_currency ?? targetCurrency,
        callSite: 'simulation_cumulative',
        // Cumulative rows must include all values from the simulation window,
        // not only values from the current target bucket.
        startDate: start,
        endDate: row.period_end ?? end
      }
    ])
    const totalAchievement = aggregateCreditedMetricValues(
      values,
      target.metrics.aggregate_function
    )

    return {
      ...row,
      total_achievement: totalAchievement,
      total_achievement_percentage:
        row.total_period_amount == null
          ? row.total_achievement_percentage
          : Targets.computeTargetAchievement(
              row.total_period_amount,
              Boolean(row.is_upper_bound),
              Boolean(row.allow_partial_outcome),
              totalAchievement
            )
    }
  }

  override async getPeriodTargetAchievement(
    targetId: number,
    start: string,
    end: string,
    profileId: string,
    effectiveDate?: string,
    currentEntityId?: number
  ) {
    const { data, error } = await this.supabase.rpc('get_period_target_achievement', {
      _target_id: targetId,
      _start: start,
      _end: end,
      _profile_id: profileId,
      _effective_date: effectiveDate,
      _source_id_limit_on_last_day: currentEntityId,
      _metrics_to_upsert: Array.from(this._metricValuesToUpsert),
      _metrics_to_exclude: Array.from(this._metricValuesToDelete)
    })

    if (error) {
      console.error(error)
    }

    const result = Array.isArray(data) && data.length > 0 ? data[0] : null
    if (!result) {
      return result
    }

    const simulated = await this.applySimulatedCreditedTargetValue(
      targetId,
      profileId,
      end,
      effectiveDate,
      currentEntityId,
      result
    )
    this._creditedTargetEvidence.set(
      this.getCreditedTargetEvidenceKey({
        targetId,
        profileId,
        start,
        end,
        effectiveDate,
        currentEntityId
      }),
      simulated.creditedEvidence
    )

    return this.applyTargetAmountOverride({
      periodEnd: result.period_end ?? end,
      periodStart: result.period_start ?? start,
      profileId,
      row: simulated.row,
      targetId
    })
  }

  override async getPeriodTargetAchievementWithEvidence(
    targetId: number,
    start: string,
    end: string,
    profileId: string,
    effectiveDate?: string,
    currentEntityId?: number
  ) {
    if (
      this.targetOverrides.length === 0 &&
      this._metricValuesToUpsert.size === 0 &&
      this._metricValuesToDelete.size === 0 &&
      this._creditSetupRuleMetricValueOverlay.size === 0
    ) {
      return super.getPeriodTargetAchievementWithEvidence(
        targetId,
        start,
        end,
        profileId,
        effectiveDate,
        currentEntityId
      )
    }

    const achievement = await this.getPeriodTargetAchievement(
      targetId,
      start,
      end,
      profileId,
      effectiveDate,
      currentEntityId
    )
    return achievement
      ? {
          ...achievement,
          creditedEvidence:
            this._creditedTargetEvidence.get(
              this.getCreditedTargetEvidenceKey({
                targetId,
                profileId,
                start,
                end,
                effectiveDate,
                currentEntityId
              })
            ) ?? []
        }
      : null
  }

  override async getUserTargetsAchievementInPeriod(
    profileId: string,
    start: string,
    end: string,
    organizationId: number,
    targetId?: number,
    onlyUserAchievementCounts?: boolean
  ) {
    const rows = await super.getUserTargetsAchievementInPeriod(
      profileId,
      start,
      end,
      organizationId,
      targetId,
      onlyUserAchievementCounts
    )

    if (
      !rows ||
      rows.length === 0 ||
      (this.targetOverrides.length === 0 &&
        this._metricValuesToUpsert.size === 0 &&
        this._metricValuesToDelete.size === 0 &&
        this._creditSetupRuleMetricValueOverlay.size === 0)
    ) {
      return rows
    }

    return Promise.all(
      rows.map(async row => {
        const simulatedRow = await this.applySimulatedCreditedCumulativeValue(
          row.target_id,
          profileId,
          start,
          end,
          row
        )

        return this.applyTargetAmountOverride({
          periodEnd: row.period_end ?? end,
          periodStart: row.period_start ?? start,
          profileId,
          row: simulatedRow,
          targetId: row.target_id
        })
      })
    )
  }

  override async getCumulativePeriodTargetAchievement(
    targetId: number,
    start: string,
    end: string,
    profileId: string,
    groupBy: string
  ) {
    const rows = await super.getCumulativePeriodTargetAchievement(
      targetId,
      start,
      end,
      profileId,
      groupBy
    )

    if (
      rows.length === 0 ||
      (this.targetOverrides.length === 0 &&
        this._metricValuesToUpsert.size === 0 &&
        this._metricValuesToDelete.size === 0 &&
        this._creditSetupRuleMetricValueOverlay.size === 0)
    ) {
      return rows
    }

    return Promise.all(
      rows.map(async row => {
        const simulatedRow = await this.applySimulatedCreditedCumulativeValue(
          targetId,
          profileId,
          start,
          end,
          row
        )
        return this.applyTargetAmountOverride({
          periodEnd: row.period_end ?? end,
          periodStart: row.period_start ?? start,
          profileId,
          row: simulatedRow,
          targetId
        })
      })
    )
  }

  override async getPeriodTargetAmount(
    targetId: number,
    start: string,
    end: string,
    profileId: string
  ): ReturnType<DolfinSupabaseDbRpc['getPeriodTargetAmount']> {
    const result = await super.getPeriodTargetAmount(targetId, start, end, profileId)
    const override = await this.resolveTargetOverride(targetId, profileId, start, end)

    if (!override) {
      return result
    }

    return {
      ...result,
      total_amount: override.targetAmount
    }
  }

  override async getTvpAllocation(
    profileId: string,
    organizationId: number,
    compensationRuleVersionId: number
  ): Promise<TvpAllocationResult> {
    const draftId = await this.getForcedCompRuleDraftId()

    if (draftId != null) {
      const draftAllocation = await this.getDraftTvpAllocation(
        profileId,
        organizationId,
        draftId,
        compensationRuleVersionId
      )

      if (draftAllocation && draftAllocation.length > 0) {
        return draftAllocation
      }
    }

    return super.getTvpAllocation(profileId, organizationId, compensationRuleVersionId)
  }

  getSimulationResults() {
    return {
      dealsToProcess: this.dealsToProcess,
      invoicesToProcess: this.invoicesToProcess,
      activitiesToProcess: this.activitiesToProcess,
      kpisToProcess: this.kpisToProcess,
      targetOverrides: this.targetOverrides,
      metricValuesToUpsert: Array.from(this._metricValuesToUpsert),
      metricValuesToDelete: Array.from(this._metricValuesToDelete),
      creditSetupRuleMetricValueOverlay: Array.from(
        this._creditSetupRuleMetricValueOverlay.entries()
      ).map(([creditSetupRuleId, identities]) => ({
        creditSetupRuleId,
        identities: Array.from(identities)
      })),
      executionResults: Object.fromEntries(this._results.entries())
    }
  }

  override async getRecordCompensationRules(
    ...params: Parameters<DolfinSupabaseDbRpc['getRecordCompensationRules']>
  ) {
    if (this.forceCompRule) {
      const { data, error } = await this.supabase
        .from('compensation_rules')
        .select(
          `
        *,
        plans!inner(
          id,
          label,
          teams_plans_history!inner(id, plan_id, team_id, effective_from, effective_to)
        ),
        compensation_rule_versions(*, compensation_rule_version_metrics(*)),
        compensation_rule_drafts(*)
      `
        )
        .eq('id', this.forceCompRule)

      if (error) {
        console.error('Error fetching forced compensation rule:', error)
        return []
      }

      return ((data ?? []) as ForcedCompRuleRow[]).map(cr => {
        this.rememberForcedCompRuleDraft(cr)

        return {
          ...cr,
          compensation_rule_versions: hydrateForcedCompRuleVersions(cr, this.forceCompRuleDiagram)
        }
      })
    }

    return super.getRecordCompensationRules(...params)
  }

  override async getPeriodCompensationRules(
    ...params: Parameters<DolfinSupabaseDbRpc['getPeriodCompensationRules']>
  ): ReturnType<DolfinSupabaseDbRpc['getPeriodCompensationRules']> {
    if (this.forceCompRule) {
      const { data, error } = await this.supabase
        .from('compensation_rules')
        .select(
          '*, plans!inner(teams_plans_history!inner(id, plan_id, team_id, effective_from, effective_to)), compensation_rule_versions(*, compensation_rule_version_metrics(*)), compensation_rule_drafts(*)'
        )
        .eq('id', this.forceCompRule)

      if (error) {
        console.error('Error fetching forced compensation rule:', error)
        return []
      }

      return ((data ?? []) as ForcedPeriodCompRuleRow[]).map(cr => {
        this.rememberForcedCompRuleDraft(cr)

        return {
          ...cr,
          compensation_rule_versions: hydrateForcedPeriodCompRuleVersions(
            cr,
            this.forceCompRuleDiagram
          )
        }
      })
    }

    return super.getPeriodCompensationRules(...params)
  }

  override async findCompensationRuleByIdForOrg(
    ...params: Parameters<DolfinSupabaseDbRpc['findCompensationRuleByIdForOrg']>
  ) {
    return super.findCompensationRuleByIdForOrg(...params)
  }

  override async getProfilesForTeamIds(
    ...params: Parameters<DolfinSupabaseDbRpc['getProfilesForTeamIds']>
  ) {
    return super.getProfilesForTeamIds(...params)
  }
}
